import React, { useEffect, useRef } from 'react'
import { Box, debounce } from '@mui/material'
import {
  createChart,
  type HistogramData,
  type IChartApi,
  type ISeriesApi,
  type UTCTimestamp,
} from 'lightweight-charts'
import { type PnlDailyRow } from './pnl-types'

interface PnlDailyBarChartProps {
  rows: PnlDailyRow[]
  height?: number
}

function toHistogramData (rows: PnlDailyRow[]): HistogramData[] {
  return rows.map((r) => {
    const value = Number(r.total)
    return {
      time: (Math.floor(new Date(`${r.day}T00:00:00Z`).getTime() / 1000)) as UTCTimestamp,
      value,
      color: value >= 0 ? '#4caf50' : '#ef5350',
    }
  })
}

const PnlDailyBarChart: React.FC<PnlDailyBarChartProps> = ({ rows, height = 260 }) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const chartRef = useRef<IChartApi | null>(null)
  const seriesRef = useRef<ISeriesApi<'Histogram'> | null>(null)

  useEffect(() => {
    if (containerRef.current === null) return

    const chart = createChart(containerRef.current, {
      layout: {
        textColor: '#e0e0e0',
        background: { color: '#121212' } as any,
      },
      grid: {
        vertLines: { color: '#2a2a2a' },
        horzLines: { color: '#2a2a2a' },
      },
      crosshair: { mode: 0 },
      rightPriceScale: { autoScale: true, borderColor: '#9d5fce' },
      timeScale: {
        fixLeftEdge: true,
        fixRightEdge: true,
        borderColor: '#9d5fce',
        timeVisible: false,
      },
      localization: {
        locale: 'en-US',
        priceFormatter: (p: number) => `$${p.toFixed(2)}`,
      },
    })
    chartRef.current = chart
    seriesRef.current = chart.addHistogramSeries({
      priceFormat: {
        type: 'custom',
        formatter: (p: number) => `$${p.toFixed(2)}`,
      },
      title: 'Daily PnL',
    })

    const ro = new ResizeObserver(debounce((entries: Array<{ contentRect: any }>) => {
      const rect = entries[0]?.contentRect
      if (rect !== undefined && chartRef.current !== null) {
        chartRef.current.resize(rect.width, rect.height)
        chartRef.current.timeScale().fitContent()
      }
    }, 50))
    ro.observe(containerRef.current)

    return () => {
      ro.disconnect()
      chart.remove()
      chartRef.current = null
    }
  }, [])

  useEffect(() => {
    if (chartRef.current === null) return
    seriesRef.current?.setData(toHistogramData(rows))
    chartRef.current.timeScale().fitContent()
  }, [rows])

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height,
        backgroundColor: '#121212',
        borderRadius: 1,
      }}
    />
  )
}

export default PnlDailyBarChart
